/**
 * The authoritative engine.
 *
 * UI, bots, the coach and the multiplayer server only ever propose actions;
 * this module decides what is legal and produces the next state plus the events
 * describing what happened. Every function is pure: no mutation of the input
 * state, no platform APIs, no hidden randomness.
 */

import { getClaimOptions, selfKongOptions } from "./claims.js";
import { createRng, shuffle } from "./rng.js";
import { claimRank, getRuleSet } from "./ruleset.js";
import type { RuleSet } from "./ruleset.js";
import { sortTiles, toKinds } from "./tiles.js";
import type { Tile } from "./tiles.js";
import type {
  Action,
  ActionError,
  ActionErrorCode,
  ApplyResult,
  ClaimAction,
  ClaimType,
  DeclaredClaim,
  DiscardRef,
  GameEvent,
  GameState,
  Meld,
  PendingClaims,
  PlayerId,
  PlayerState,
  SeatWind,
} from "./types.js";
import { PLAYER_IDS, SEAT_WINDS } from "./types.js";

export interface CreateGameOptions {
  ruleSetId?: string;
  /** Seat that receives the extra tile and moves first. Defaults to seat 0 (East). */
  dealer?: PlayerId;
}

function fail(code: ActionErrorCode, message: string): { ok: false; error: ActionError } {
  return { ok: false, error: { code, message } };
}

export function isPlayerId(value: unknown): value is PlayerId {
  return value === 0 || value === 1 || value === 2 || value === 3;
}

export function nextPlayer(playerId: PlayerId): PlayerId {
  return ((playerId + 1) % 4) as PlayerId;
}

/** Seats between `from` and `to` going around the table; 0 when they are equal. */
export function seatDistance(from: PlayerId, to: PlayerId): number {
  return (to - from + 4) % 4;
}

export function getPlayer(state: GameState, playerId: PlayerId): PlayerState {
  return state.players[playerId];
}

/** Hand plus the freshly drawn tile: everything the player may discard. */
export function getConcealedTiles(player: PlayerState): Tile[] {
  return player.drawnTile ? [...player.hand, player.drawnTile] : [...player.hand];
}

export function getConcealedKinds(player: PlayerState) {
  return toKinds(getConcealedTiles(player));
}

export function wallRemaining(state: GameState): number {
  return state.wall.length;
}

export function isGameOver(state: GameState): boolean {
  return state.phase === "ended" || state.result !== null;
}

/** How many concealed tiles the player should hold between turns. */
export function expectedHandSize(state: GameState, player: PlayerState): number {
  return getRuleSet(state.ruleSetId).handSize - 3 * player.melds.length;
}

function seatFor(playerId: PlayerId, dealer: PlayerId): SeatWind {
  return SEAT_WINDS[seatDistance(dealer, playerId)] as SeatWind;
}

function withPlayer(state: GameState, player: PlayerState): GameState["players"] {
  const players = state.players.slice() as GameState["players"];
  players[player.id] = player;
  return players;
}

/**
 * Deal a fresh game. Same seed + same rule set always produces the same wall,
 * the same hands and therefore the same game.
 */
export function createGame(seed: string | number, options: CreateGameOptions = {}): GameState {
  const ruleSet: RuleSet = getRuleSet(options.ruleSetId);
  const dealer: PlayerId = options.dealer ?? 0;
  const seedString = String(seed);

  const shuffled = shuffle(ruleSet.buildWall(), createRng(seedString));
  let cursor = 0;
  const hands: Tile[][] = [[], [], [], []];

  // Classical deal: 4 + 4 + 4 + 1 tiles per player, starting from the dealer.
  for (const chunk of [4, 4, 4, 1]) {
    for (let offset = 0; offset < 4; offset += 1) {
      const playerId = ((dealer + offset) % 4) as PlayerId;
      const target = hands[playerId] as Tile[];
      target.push(...shuffled.slice(cursor, cursor + chunk));
      cursor += chunk;
    }
  }

  // The dealer's extra tile is held as a drawn tile, so the very first move is
  // a discard and the draw/discard invariant holds from the start.
  const dealerTile = ruleSet.dealerExtraTiles > 0 ? (shuffled[cursor] as Tile) : null;
  cursor += ruleSet.dealerExtraTiles;

  const players = PLAYER_IDS.map((playerId) => {
    const player: PlayerState = {
      id: playerId,
      seat: seatFor(playerId, dealer),
      hand: sortTiles(hands[playerId] as Tile[]),
      drawnTile: playerId === dealer ? dealerTile : null,
      discards: [],
      melds: [],
    };
    return player;
  }) as GameState["players"];

  const rest = shuffled.slice(cursor);
  // The dead wall (kong replacements) is reserved at the tail.
  const deadWallStart = rest.length - ruleSet.deadWallSize;

  return {
    version: 2,
    ruleSetId: ruleSet.id,
    seed: seedString,
    dealer,
    turn: dealer,
    phase: dealerTile ? "awaiting-discard" : "awaiting-draw",
    wall: rest.slice(0, deadWallStart),
    deadWall: rest.slice(deadWallStart),
    players,
    lastDiscard: null,
    pendingClaims: null,
    actionCount: 0,
    result: null,
  };
}

/**
 * Every action `playerId` may legally take right now.
 * Humans, bots, the coach and the server all go through this; nothing else is
 * accepted by `applyAction`.
 */
export function getLegalActions(state: GameState, playerId: PlayerId): Action[] {
  if (!isPlayerId(playerId)) return [];
  if (isGameOver(state)) return [];

  if (state.phase === "awaiting-claims") {
    const pending = state.pendingClaims;
    if (!pending || !pending.waitingOn.includes(playerId)) return [];
    return [...getClaimOptions(state, playerId, pending.discard), { type: "pass", playerId }];
  }

  if (state.turn !== playerId) return [];
  const player = getPlayer(state, playerId);

  if (state.phase === "awaiting-draw") {
    return state.wall.length > 0 ? [{ type: "draw", playerId }] : [];
  }

  const actions: Action[] = [];
  const ruleSet = getRuleSet(state.ruleSetId);
  if (
    player.drawnTile !== null &&
    ruleSet.analyzeWin(getConcealedKinds(player), player.melds.length) !== null
  ) {
    actions.push({ type: "declare-win", playerId });
  }
  actions.push(...selfKongOptions(state, playerId));
  for (const tile of getConcealedTiles(player)) {
    actions.push({ type: "discard", playerId, tileId: tile.id });
  }
  return actions;
}

function sameTileIds(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].sort((x, y) => x - y);
  const right = [...b].sort((x, y) => x - y);
  return left.every((value, index) => value === right[index]);
}

function matchLegalClaim(options: readonly ClaimAction[], action: ClaimAction): ClaimAction | null {
  return (
    options.find(
      (option) => option.type === action.type && sameTileIds(option.tileIds, action.tileIds ?? []),
    ) ?? null
  );
}

/** Take specific tiles out of the concealed hand; null when any is missing. */
function takeTiles(
  concealed: readonly Tile[],
  tileIds: readonly number[],
): { taken: Tile[]; rest: Tile[] } | null {
  const taken: Tile[] = [];
  const rest = [...concealed];
  for (const tileId of tileIds) {
    const index = rest.findIndex((tile) => tile.id === tileId);
    if (index === -1) return null;
    taken.push(rest[index] as Tile);
    rest.splice(index, 1);
  }
  return { taken, rest };
}

/**
 * Pull one replacement tile for a kong. The dead wall keeps its size by taking
 * the last live-wall tile, so each kong costs the table one normal draw.
 */
function drawReplacement(state: GameState): { tile: Tile; wall: Tile[]; deadWall: Tile[] } | null {
  const tile = state.deadWall[0];
  if (!tile) return null;
  const deadWall = state.deadWall.slice(1);
  const wall = state.wall.slice();
  const moved = wall.pop();
  if (moved) deadWall.push(moved);
  return { tile, wall, deadWall };
}

function endInExhaustiveDraw(state: GameState, events: GameEvent[]): ApplyResult {
  events.push({ type: "exhaustive-draw" });
  return {
    ok: true,
    state: {
      ...state,
      phase: "ended",
      pendingClaims: null,
      result: { type: "exhaustive-draw" },
    },
    events,
  };
}

/** Hand the turn to the next player, or end the hand when the wall is spent. */
function passTurnAfterDiscard(state: GameState, discarder: PlayerId, events: GameEvent[]): ApplyResult {
  if (state.wall.length === 0) {
    return endInExhaustiveDraw(state, events);
  }
  const turn = nextPlayer(discarder);
  events.push({ type: "turn-changed", playerId: turn });
  return {
    ok: true,
    state: { ...state, phase: "awaiting-draw", turn, pendingClaims: null },
    events,
  };
}

function meldFromClaim(
  claim: DeclaredClaim,
  taken: Tile[],
  claimedTile: Tile,
  from: PlayerId,
): Meld {
  const tiles = sortTiles([...taken, claimedTile]);
  if (claim.type === "kong") {
    return { kind: "kong", tiles, concealed: false, claimedFrom: from, kongKind: "claimed" };
  }
  return {
    kind: claim.type === "pong" ? "pong" : "chow",
    tiles,
    concealed: false,
    claimedFrom: from,
  };
}

/** Decide which of the declared claims wins, using the rule set's priority. */
export function pickWinningClaim(
  state: GameState,
  pending: PendingClaims,
): DeclaredClaim | null {
  const ruleSet = getRuleSet(state.ruleSetId);
  const ranked = [...pending.declared].sort((a, b) => {
    const byPriority = claimRank(ruleSet, a.type) - claimRank(ruleSet, b.type);
    if (byPriority !== 0) return byPriority;
    // Same priority: the seat closest after the discarder wins (head bump).
    return (
      seatDistance(pending.discard.playerId, a.playerId) -
      seatDistance(pending.discard.playerId, b.playerId)
    );
  });
  return ranked[0] ?? null;
}

function applyWin(
  state: GameState,
  winner: PlayerId,
  by: "self-draw" | "discard",
  from: PlayerId | null,
  winningTile: Tile,
  events: GameEvent[],
): ApplyResult {
  const ruleSet = getRuleSet(state.ruleSetId);
  const player = getPlayer(state, winner);
  const kinds =
    by === "self-draw"
      ? getConcealedKinds(player)
      : [...toKinds(player.hand), winningTile.kind];
  const hand = ruleSet.analyzeWin(kinds, player.melds.length);
  if (!hand) {
    return fail("NOT_A_WINNING_HAND", "Those tiles are not four sets plus a pair.");
  }

  events.push({
    type: "won",
    playerId: winner,
    by,
    from,
    winningTile: winningTile.kind,
    hand,
  });
  return {
    ok: true,
    state: {
      ...state,
      phase: "ended",
      pendingClaims: null,
      actionCount: state.actionCount + 1,
      result: { type: "win", winner, by, from, winningTile: winningTile.kind, hand },
    },
    events,
  };
}

/** Everyone has answered the claim window: apply the winning claim, or move on. */
function resolveClaimWindow(state: GameState, events: GameEvent[]): ApplyResult {
  const pending = state.pendingClaims;
  if (!pending) return fail("WRONG_PHASE", "There is no open claim window.");

  const winning = pickWinningClaim(state, pending);
  const discarder = pending.discard.playerId;
  if (!winning) {
    return passTurnAfterDiscard(state, discarder, events);
  }

  const claimer = getPlayer(state, winning.playerId);
  const claimedTile = pending.discard.tile;

  if (winning.type === "ron") {
    return applyWin(state, winning.playerId, "discard", discarder, claimedTile, events);
  }

  const split = takeTiles(claimer.hand, winning.tileIds);
  if (!split) {
    return fail("TILE_NOT_IN_HAND", "The claimed tiles are no longer in hand.");
  }

  const meld = meldFromClaim(winning, split.taken, claimedTile, discarder);
  const discarderState = getPlayer(state, discarder);
  const updatedDiscarder: PlayerState = {
    ...discarderState,
    // The claimed tile leaves the discard pile: it is now part of a meld.
    discards: discarderState.discards.filter((tile) => tile.id !== claimedTile.id),
  };
  const updatedClaimer: PlayerState = {
    ...claimer,
    hand: sortTiles(split.rest),
    drawnTile: null,
    melds: [...claimer.melds, meld],
  };

  let players = withPlayer(state, updatedDiscarder);
  players = withPlayer({ ...state, players }, updatedClaimer);

  events.push({
    type: "meld-formed",
    playerId: winning.playerId,
    meld,
    claimedTile,
  });

  let next: GameState = {
    ...state,
    players,
    lastDiscard: null,
    pendingClaims: null,
    turn: winning.playerId,
    phase: "awaiting-discard",
  };

  if (winning.type === "kong") {
    const replacement = drawReplacement(next);
    if (!replacement) {
      return endInExhaustiveDraw(next, events);
    }
    const withReplacement: PlayerState = {
      ...updatedClaimer,
      drawnTile: replacement.tile,
    };
    next = {
      ...next,
      wall: replacement.wall,
      deadWall: replacement.deadWall,
      players: withPlayer(next, withReplacement),
    };
    events.push({
      type: "drew",
      playerId: winning.playerId,
      tile: replacement.tile,
      wallRemaining: next.wall.length,
      replacement: true,
    });
  }

  events.push({ type: "turn-changed", playerId: winning.playerId });
  return { ok: true, state: next, events };
}

/** A kong declared from the player's own hand: concealed, or added to a pong. */
function applySelfKong(state: GameState, action: ClaimAction, events: GameEvent[]): ApplyResult {
  const player = getPlayer(state, action.playerId);
  const options = selfKongOptions(state, action.playerId);
  const matched = matchLegalClaim(options, action);
  if (!matched) {
    return fail("ILLEGAL_CLAIM", "That kong is not available right now.");
  }

  const concealed = getConcealedTiles(player);
  const split = takeTiles(concealed, matched.tileIds);
  if (!split) return fail("TILE_NOT_IN_HAND", "Those tiles are not in hand.");

  const isAdded = matched.tileIds.length === 1;
  let melds: Meld[];
  let meld: Meld;

  if (isAdded) {
    const extra = split.taken[0] as Tile;
    const index = player.melds.findIndex(
      (existing) => existing.kind === "pong" && existing.tiles[0]?.kind === extra.kind,
    );
    if (index === -1) return fail("ILLEGAL_CLAIM", "There is no matching pong to extend.");
    const base = player.melds[index] as Meld;
    meld = {
      ...base,
      kind: "kong",
      tiles: sortTiles([...base.tiles, extra]),
      kongKind: "added",
    };
    melds = player.melds.map((existing, position) => (position === index ? meld : existing));
  } else {
    meld = {
      kind: "kong",
      tiles: sortTiles(split.taken),
      concealed: true,
      claimedFrom: null,
      kongKind: "concealed",
    };
    melds = [...player.melds, meld];
  }

  const updated: PlayerState = {
    ...player,
    hand: sortTiles(split.rest),
    drawnTile: null,
    melds,
  };

  let next: GameState = {
    ...state,
    players: withPlayer(state, updated),
    actionCount: state.actionCount + 1,
  };
  events.push({ type: "meld-formed", playerId: action.playerId, meld, claimedTile: null });

  const replacement = drawReplacement(next);
  if (!replacement) {
    return endInExhaustiveDraw(next, events);
  }
  next = {
    ...next,
    wall: replacement.wall,
    deadWall: replacement.deadWall,
    players: withPlayer(next, { ...updated, drawnTile: replacement.tile }),
    phase: "awaiting-discard",
  };
  events.push({
    type: "drew",
    playerId: action.playerId,
    tile: replacement.tile,
    wallRemaining: next.wall.length,
    replacement: true,
  });

  return { ok: true, state: next, events };
}

/**
 * Validate and apply one action.
 * Never throws on an illegal action and never mutates `state`; illegal actions
 * come back as `{ ok: false, error }` so clients can show why.
 */
export function applyAction(state: GameState, action: Action): ApplyResult {
  if (isGameOver(state)) {
    return fail("GAME_ENDED", "The game has already ended.");
  }
  if (!isPlayerId(action.playerId)) {
    return fail("UNKNOWN_PLAYER", `Unknown player: ${String(action.playerId)}`);
  }

  const ruleSet = getRuleSet(state.ruleSetId);
  const events: GameEvent[] = [];

  // ---- claim window -------------------------------------------------------
  if (state.phase === "awaiting-claims") {
    const pending = state.pendingClaims;
    if (!pending) return fail("WRONG_PHASE", "There is no open claim window.");
    if (!pending.waitingOn.includes(action.playerId)) {
      return fail(
        "NOT_IN_CLAIM_WINDOW",
        `Player ${action.playerId} has no pending claim decision.`,
      );
    }
    if (action.type !== "pass" && !isClaimType(action.type)) {
      return fail("WRONG_PHASE", `Cannot ${action.type} while a claim window is open.`);
    }

    let declared = pending.declared;
    if (action.type === "pass") {
      events.push({ type: "passed", playerId: action.playerId });
    } else {
      const options = getClaimOptions(state, action.playerId, pending.discard);
      const matched = matchLegalClaim(options, action as ClaimAction);
      if (!matched) {
        return fail("ILLEGAL_CLAIM", `Player ${action.playerId} cannot ${action.type} that tile.`);
      }
      declared = [
        ...declared,
        { playerId: action.playerId, type: matched.type, tileIds: matched.tileIds },
      ];
      events.push({ type: "claim-declared", playerId: action.playerId, claim: matched.type });
    }

    const waitingOn = pending.waitingOn.filter((id) => id !== action.playerId);
    const next: GameState = {
      ...state,
      actionCount: state.actionCount + 1,
      pendingClaims: { ...pending, waitingOn, declared },
    };
    if (waitingOn.length > 0) {
      return { ok: true, state: next, events };
    }
    return resolveClaimWindow(next, events);
  }

  // ---- normal turn --------------------------------------------------------
  if (state.turn !== action.playerId) {
    return fail(
      "NOT_YOUR_TURN",
      `It is player ${state.turn}'s turn, not player ${action.playerId}'s.`,
    );
  }

  const player = getPlayer(state, action.playerId);

  switch (action.type) {
    case "draw": {
      if (state.phase !== "awaiting-draw") {
        return fail("WRONG_PHASE", `Cannot draw while phase is "${state.phase}".`);
      }
      const tile = state.wall[0];
      if (!tile) return fail("WALL_EMPTY", "The wall is empty.");

      const nextState: GameState = {
        ...state,
        wall: state.wall.slice(1),
        players: withPlayer(state, { ...player, drawnTile: tile }),
        phase: "awaiting-discard",
        actionCount: state.actionCount + 1,
      };
      events.push({
        type: "drew",
        playerId: action.playerId,
        tile,
        wallRemaining: nextState.wall.length,
        replacement: false,
      });
      return { ok: true, state: nextState, events };
    }

    case "discard": {
      if (state.phase !== "awaiting-discard") {
        return fail("WRONG_PHASE", `Cannot discard while phase is "${state.phase}".`);
      }
      const drawn = player.drawnTile;
      const fromDrawn = drawn !== null && drawn.id === action.tileId;
      const handTile = player.hand.find((tile) => tile.id === action.tileId) ?? null;
      if (!fromDrawn && !handTile) {
        return fail(
          "TILE_NOT_IN_HAND",
          `Player ${action.playerId} does not hold tile ${action.tileId}.`,
        );
      }

      const discarded = (fromDrawn ? drawn : handTile) as Tile;
      const hand = fromDrawn
        ? player.hand
        : sortTiles([
            ...player.hand.filter((tile) => tile.id !== action.tileId),
            ...(drawn ? [drawn] : []),
          ]);

      const updated: PlayerState = {
        ...player,
        hand,
        drawnTile: null,
        discards: [...player.discards, discarded],
      };

      events.push({
        type: "discarded",
        playerId: action.playerId,
        tile: discarded,
        fromHand: !fromDrawn,
      });

      const discard: DiscardRef = { playerId: action.playerId, tile: discarded };
      const base: GameState = {
        ...state,
        players: withPlayer(state, updated),
        lastDiscard: discard,
        pendingClaims: null,
        actionCount: state.actionCount + 1,
      };

      // Anyone with a legal claim interrupts the normal turn order.
      const waitingOn = ruleSet.allowsClaims
        ? PLAYER_IDS.filter(
            (id) => id !== action.playerId && getClaimOptions(base, id, discard).length > 0,
          ).sort((a, b) => seatDistance(action.playerId, a) - seatDistance(action.playerId, b))
        : [];

      if (waitingOn.length > 0) {
        events.push({ type: "claim-window-opened", discard, waitingOn });
        return {
          ok: true,
          state: {
            ...base,
            phase: "awaiting-claims",
            pendingClaims: { discard, waitingOn, declared: [] },
          },
          events,
        };
      }

      return passTurnAfterDiscard(base, action.playerId, events);
    }

    case "declare-win": {
      if (state.phase !== "awaiting-discard") {
        return fail("WRONG_PHASE", `Cannot declare a win while phase is "${state.phase}".`);
      }
      const winningTile = player.drawnTile;
      if (!winningTile) {
        return fail("WRONG_PHASE", "A win is declared on the tile just drawn.");
      }
      return applyWin(state, action.playerId, "self-draw", null, winningTile, events);
    }

    case "kong": {
      if (ruleSet.kongReplacement === "none") {
        return fail("CLAIM_NOT_SUPPORTED", `Rule set "${ruleSet.id}" does not support kongs.`);
      }
      if (state.phase !== "awaiting-discard") {
        return fail("WRONG_PHASE", `Cannot declare a kong while phase is "${state.phase}".`);
      }
      return applySelfKong(state, action, events);
    }

    case "chow":
    case "pong":
    case "ron": {
      if (!ruleSet.allowsClaims) {
        return fail(
          "CLAIM_NOT_SUPPORTED",
          `Rule set "${ruleSet.id}" does not support discard claims.`,
        );
      }
      return fail("WRONG_PHASE", `${action.type} is only possible while a discard is on offer.`);
    }

    case "pass": {
      return fail("WRONG_PHASE", "There is nothing to pass on right now.");
    }

    default: {
      const unknown = action as { type?: unknown };
      return fail("UNKNOWN_ACTION", `Unknown action type: ${String(unknown.type)}`);
    }
  }
}

function isClaimType(value: string): value is ClaimType {
  return value === "chow" || value === "pong" || value === "kong" || value === "ron";
}
